import styled, { css } from "styled-components";
import { NavLink as BaseNavLink } from "react-router-dom";
import ContentLayout from "components/layout/ContentLayout";
import {
  colors,
  fonts,
  mediaMin,
  mediaMax,
  shadows,
  fontSizesUI,
  fontWeights,
  zIndex,
  transitions,
} from "utils/theme";
import { ButtonLink } from "../Button";

export const LogoWrapper = styled.div`
  padding: 16px;
  flex: 0 0 auto;

  svg {
    width: 108px;
    display: block;
  }

  ${mediaMax("lg")} {
    display: none;
  }
`;

export const Wrapper = styled.nav`
  max-width: 480px;
  width: 100%;
  margin: 0 auto;

  background: ${colors.midnight};
  color: white;

  ${mediaMin("lg")} {
    display: flex;
    max-width: none;
    padding: 24px;
    height: 100vh;

    box-shadow: ${shadows.tile};
  }
`;

export const NavInner = styled(ContentLayout)`
  ${mediaMin("lg")} {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
  }
`;

export const NavItem = styled.li<{ $hideMobile?: boolean }>`
  display: flex;
  align-items: center;

  list-style-type: none;

  ${(props) =>
    props.$hideMobile &&
    css`
      ${mediaMax("lg")} {
        display: none;
      }
    `}
`;

export const NavLink = styled(BaseNavLink)``;

export const Label = styled.div`
  font-size: ${fontSizesUI.md}px;
  line-height: 1.33;
`;

export const ShortLabel = styled.div`
  font-size: ${fontSizesUI.sm}px;

  ${mediaMin("lg")} {
    display: none;
  }
`;

export const NavItems = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  flex: 1 1 auto;

  ${mediaMax("lg")} {
    position: relative;

    padding-right: 24px;
    flex-direction: row;
    justify-content: center;
    gap: 16px;
  }

  ${NavItem} {
    ${mediaMax("lg")} {
      width: 96px;
      justify-content: center;

      text-align: center;
    }
  }

  ${NavLink} {
    display: flex;
    gap: 2px;
    flex-direction: column;
    align-items: center;

    color: white;

    font-family: ${fonts.body};
    font-weight: ${fontWeights.normal};
    text-decoration: none;

    ${mediaMin("lg")} {
      width: 100%;
      padding: 16px;
      border-radius: 12px;
      flex-direction: row;
      gap: 8px;

      &.active {
        background: ${colors.azure};
      }

      &:not(.active) {
        ${Label} {
          opacity: 0.7;
        }
      }
    }

    ${mediaMax("lg")} {
      width: 80px;
      padding: 8px;
      margin: -8px;

      border-radius: 8px;

      &.active {
        background: ${colors.flax};
        color: ${colors.violet};

        svg {
          * {
            fill: ${colors.violet};
          }
        }
      }
    }
  }

  ${Label} {
    ${mediaMax("lg")} {
      display: none;
    }
  }
`;

export const MobileNavItems = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin: 0;
  padding: 0;

  ${NavLink} {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    width: 100%;
    padding: 16px;
    border-radius: 12px;

    color: ${colors.violet};

    font-family: ${fonts.body};
    font-weight: ${fontWeights.bold};
    text-decoration: none;
    text-align: left;

    &.active {
      background: ${colors.flax};
    }
  }
`;

export const MobileMenuButton = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 24px;

  ${mediaMin("lg")} {
    display: none;
  }
`;

export const ExportDataButtonWrapper = styled.li`
  margin-top: 16px;
  width: 100%;

  list-style-type: none;

  button {
    background: ${colors.heliotrope};
    color: white;
  }

  ${mediaMax("lg")} {
    display: none;
  }
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 0 0 auto;

  ${mediaMax("lg")} {
    display: none;
  }
`;

export const AdminButtonLink = styled(ButtonLink)`
  border-color: ${colors.heliotrope};
  background: transparent;
  color: white !important;
`;

export const MobileLinks = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 100%;
  z-index: ${zIndex.nav};

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 24px;
  height: 100vh;
  width: 400px;
  max-width: 100%;
  gap: 16px;

  background: white;
  opacity: 0;
  visibility: hidden;

  transition: all ${transitions.default};

  & > * {
    width: 100%;
  }

  ${(props) =>
    props.$isOpen &&
    css`
      transform: translateX(-100%);

      opacity: 1;
      visibility: visible;
      box-shadow: -4px 0 24px ${colors.violet}20;
    `}

  ${mediaMin("lg")} {
    display: none;
  }
`;

export const ButtonsWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const MobileCloseButton = styled.div`
  padding-top: 24px;
  width: auto;
`;
